import { auroraStore } from '../../store/auroraStore'
import { play } from '../../lib/feedback'
import { cardItem, fromCalendarDate, toCalendarDate, type Card } from '@aurora/core'
import { CardFaceMini, DeckCover } from './DeckCover'
import type { useCards } from './useCards'

type Cards = ReturnType<typeof useCards>

/**
 * Today's cards: one Start that plays every deck.
 *
 * The method is ten cards from each deck, every deck, every day, so the
 * parent's whole job here is one tap with her on their lap. Each deck's line
 * says what today holds for it (reviews and new cards, from the same plan
 * the session will play), and the week of dots is a quiet record of the days
 * it happened: something to notice, never a chain to keep alive.
 */
export function TodayCards({ cards, onNewDeck, compact, lead = true }: {
  cards: Cards
  onNewDeck: () => void
  /** Headline and Start only: where the decks are listed elsewhere on the page. */
  compact?: boolean
  /** The one filled button on the page; beside another thing to hand her, the quieter one. */
  lead?: boolean
}) {
  const { child, decks, active, session, streak, toPlay, doneToday, partway } = cards
  const name = child?.name ?? 'her'
  const making = decks.filter((d) => d.status === 'generating')

  const start = () => {
    if (!session) return
    play('tap')
    ;(auroraStore.getState() as any).enterKidMode(session.anchor._id, undefined, {
      html: session.html,
      assets: session.assets,
      title: "Today's cards",
    })
  }

  if (!decks.length) {
    return (
      <section className="today-cards is-empty reveal">
        <p className="today-cards-kicker">A few cards, every day</p>
        <h2 className="today-cards-title">Start {name}'s first deck</h2>
        <p className="today-cards-body">
          About ten cards a deck, a few minutes a day. She reads, you tap whether she got it, and
          each card comes back just before she would forget it.
        </p>
        <button className="btn btn-primary btn-lg today-cards-start" onClick={onNewDeck}>
          <PlusIcon /> Start a deck
        </button>
      </section>
    )
  }

  // With nothing to play, say why: a deck on its way, decks resting, or
  // decks that did not come back (a failed deck is not resting).
  const resting = decks.filter((d) => d.deck?.paused).length
  const failed = decks.filter((d) => d.status === 'failed').length
  // Partway, the news is how many are left, from the same day plan each deck's line reads.
  const left = active.reduce((n, d) => n + Math.max(0, (d.day?.target ?? 0) - (d.day?.doneToday ?? 0)), 0)
  const [headline, sub] = doneToday
    ? ['All done for today']
    : active.length
      ? [partway && left ? `${left} ${left === 1 ? 'card' : 'cards'} left today` : `${toPlay} ${toPlay === 1 ? 'card' : 'cards'} today`, active.length > 1 ? `From ${active.length} decks, one after another` : undefined]
      : making.length
        ? [resting ? 'A new deck is on its way' : 'Her first deck is on its way']
        : resting
          ? [resting > 1 ? 'Every deck is resting' : 'Her deck is resting', `Open ${resting > 1 ? 'one' : 'it'} below to play it again`]
          : [failed > 1 ? 'Her decks did not come back' : 'Her deck did not come back', 'Try again below, or start a different one']

  return (
    <section className={`today-cards reveal${doneToday ? ' is-done' : ''}`}>
      <header className="today-cards-head">
        <div>
          <p className="today-cards-kicker">Today's cards</p>
          <h2 className="today-cards-title tnum">{headline}</h2>
          {sub && <p className="today-cards-sub">{sub}</p>}
        </div>
        <Streak streak={streak} />
      </header>

      {/* Side by side with the reading step, the decks today's session plays stand where its picture would. */}
      {compact && active.length > 0 && (
        <ul className="today-cards-covers" aria-label={`Today: ${active.map((d) => d.deck?.title ?? d.artifact.title).join(', ')}`}>
          {active.slice(0, 5).map((d, i) => (
            <li key={d.artifact._id} style={{ '--i': i } as React.CSSProperties} title={d.deck?.title ?? d.artifact.title}>
              <DeckCover deck={d.deck} title={d.artifact.title} size={56} />
              <span className="today-cards-cover-name" dir="auto">{d.deck?.title ?? d.artifact.title}</span>
            </li>
          ))}
          {active.length > 5 && <li className="today-cards-covers-more tnum">+{active.length - 5}</li>}
        </ul>
      )}

      {/* What today holds, from the day plans each deck's own line reads, so the card says what the button plays. */}
      {compact && active.length > 0 && !doneToday && !partway && (
        <p className="today-cards-mix tnum">
          {todayLine(active.reduce((n, d) => n + (d.day ? d.day.due + d.day.practice : 0), 0), active.reduce((n, d) => n + (d.day?.fresh ?? 0), 0))}
          {' · '}about {cards.minutes} {cards.minutes === 1 ? 'minute' : 'minutes'}
        </p>
      )}

      {compact && !doneToday && <UpFirst cards={cards} />}

      {session && (
        <button
          className={`btn ${doneToday || !lead ? 'btn-ghost' : 'btn-primary'} btn-lg today-cards-start`}
          onClick={start}
        >
          <PlayIcon /> {doneToday ? 'Play a few more cards' : partway ? 'Finish her cards' : 'Play her cards'}
        </button>
      )}

      {!compact && <ul className="today-decks">
        {decks.map(({ artifact, deck, status, day }) => (
          <li key={artifact._id} className={`today-deck is-${status}`}>
            <DeckCover deck={deck} title={artifact.title} size={36} />
            <span className="today-deck-title" dir="auto">{deck?.title ?? artifact.title}</span>
            <span className="today-deck-what tnum">
              {status === 'generating' ? (
                <span className="today-deck-making">Being made</span>
              ) : status === 'failed' ? (
                'Did not come back'
              ) : deck?.paused ? (
                'Resting'
              ) : day?.finished ? (
                <span className="today-deck-done">
                  <CheckIcon /> {day.doneToday} done
                </span>
              ) : day && day.doneToday > 0 ? (
                `${day.doneToday} done · ${day.target - day.doneToday} to go`
              ) : day ? (
                todayLine(day.due + day.practice, day.fresh)
              ) : null}
            </span>
          </li>
        ))}
      </ul>}
    </section>
  )
}

/**
 * The cards she meets next, face up, from the same day plans the session
 * plays: one per deck in turn, leaving out any she has already answered
 * today. Beside the reading step's picture, the parent sees what the cards
 * hold before handing them over.
 */
function UpFirst({ cards }: { cards: Cards }) {
  const dayStart = fromCalendarDate(toCalendarDate(cards.now, cards.zone), cards.zone)
  const queues = cards.active.map((d) => ({
    row: d,
    items: (d.day?.plan ?? []).map((c) => c.item).filter((k, i, all) => all.indexOf(k) === i && (cards.memory[k]?.last ?? 0) < dayStart),
  }))
  const faces: Array<{ key: string; face: Card['front']; assets: any }> = []
  for (let round = 0; faces.length < UP_FIRST && queues.some((q) => q.items.length > round); round++) {
    for (const { row, items } of queues) {
      const item = items[round]
      const card = item && row.deck?.cards.find((c) => cardItem(row.deck!.id, c.id).toLowerCase() === item)
      if (card && faces.length < UP_FIRST) faces.push({ key: item, face: card.kind === 'generator' ? { text: '123' } : card.front, assets: row.artifact.assets })
    }
  }
  if (!faces.length) return null
  return (
    <div className="today-cards-up">
      <p className="today-cards-up-k">Up first</p>
      <ul className="today-cards-up-faces">
        {faces.map((f, i) => (
          <li key={f.key} className="today-cards-up-card" style={{ '--i': i } as React.CSSProperties}>
            <CardFaceMini face={f.face} assets={f.assets} />
          </li>
        ))}
      </ul>
    </div>
  )
}
const UP_FIRST = 5

function todayLine(reviews: number, fresh: number): string {
  const parts = [
    reviews ? `${reviews} to review` : '',
    fresh ? `${fresh} new` : '',
  ].filter(Boolean)
  return parts.join(' · ') || 'Nothing due'
}

/** Seven dots, today last, and the run in words. Quiet on purpose. */
function Streak({ streak }: { streak: Cards['streak'] }) {
  const days = streak.streak
  const label = days >= 2 ? `${days} days in a row` : days === 1 ? (streak.doneToday ? 'Played today' : 'Last played yesterday') : 'The last 7 days'
  return (
    <div className="streak" aria-label={`${label}. Cards on ${streak.week.filter((d) => d.done).length} of the last 7 days.`}>
      <div className="streak-dots" aria-hidden="true">
        {streak.week.map((d, i) => (
          <span key={d.date} className={`streak-dot${d.done ? ' is-on' : ''}${i === 6 ? ' is-today' : ''}`} title={d.date} />
        ))}
      </div>
      <span className="streak-label tnum">{label}</span>
    </div>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.1,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

export const PlusIcon = () => (
  <svg {...S} width="16" height="16">
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const PlayIcon = () => (
  <svg {...S} width="16" height="16" fill="currentColor" strokeWidth={1.5}>
    <path d="M8 5.5v13l10.5-6.5z" />
  </svg>
)

export const CheckIcon = () => (
  <svg {...S} width="13" height="13">
    <path d="M5 12.5l4.2 4L19 7" />
  </svg>
)
